'use client';

import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Activity, Calendar, ChevronDown, ChevronLeft, Clock, Image as ImageIcon, Instagram, Copy, Download, Lock, MapPin, Users, X } from 'lucide-react';
import type { ApplePaySheet, DoneScroll, PhonePress, PhoneScreen } from '@/lib/website-hero/timeline';
import { PHONE_SCREEN_H, PHONE_SCREEN_W } from '@/lib/website-hero/geometry';
import { ATHLETE, EVENT, ORG, THEME, TICKETS } from '@/lib/website-hero/est28';
import { PASS_QR } from '@/lib/website-hero/pass-qr';

/**
 * The athlete's phone: est.28's public pages in Safari, drawn at a real
 * 390 x 844 iPhone screen and scaled into the device by the parent.
 *
 * The pages are built from the product's own markup and theme maths:
 *   - event page    ThemedEventPage (header), V2EventHero, CoverImage,
 *                   DescriptionSection
 *   - ticket sheet  V2MobileTicketBar wrapping V2TicketPanel's simple list,
 *                   the same element collapsed and expanded, with the real
 *                   0.5s sheet easing
 *   - payment       SplitPayCaptainCheckout, captain paying for the pair, with
 *                   the white Apple Pay button Stripe renders on a dark page
 *   - confirmation  app/e/[slug]/checkout/success, including the expanded
 *                   InstagramStoryShare and the real wallet badges
 *
 * Some of it is not a copy at all. The story card is drawn by the product's
 * own renderStoryToCanvas, and the email is the product's own
 * AttendeeConfirmationEmail with est.28's resolved branding. Both load after
 * the phone first appears, so the page's first paint doesn't carry them.
 *
 * The system pieces (Safari's chrome, the Apple Pay sheet, the Wallet add
 * sheet, the notification and Mail) are iOS's, drawn to match.
 */

/* ── est.28's theme, resolved the way ThemedEventPage resolves it ── */

const PAGE_BG = THEME.ground;
const ACCENT = THEME.accent;
const ON_ACCENT = THEME.onAccent;
const TEXT = THEME.text;
const TEXT_MUTED = THEME.textMuted;

const THEME_VARS = {
  '--theme-secondary': ACCENT,
  '--button-text': ON_ACCENT,
  '--fv-accent': ACCENT,
  '--fv-accent-bg': `${ACCENT}1F`,
  '--fv-text-primary': TEXT,
  '--fv-text-secondary': TEXT_MUTED,
  '--fv-text-muted': TEXT_MUTED,
  // Transparent box: borders are the page text at 20%.
  '--fv-border': `${TEXT}33`,
  '--fv-border-strong': `${TEXT}4D`,
  '--fv-bg-raised': `color-mix(in srgb, ${PAGE_BG}, #ffffff 8%)`,
  '--fv-bg-secondary': `color-mix(in srgb, ${PAGE_BG}, #ffffff 8%)`,
  '--fv-theme-text': TEXT,
  '--fv-theme-text-muted': TEXT_MUTED,
} as CSSProperties;

/** est.28 uses Barlow Condensed for headings and body alike. */
const FONT = 'var(--font-barlow-condensed), "Arial Narrow", sans-serif';
const MONO = 'var(--font-geist-mono), ui-monospace, monospace';
const SYSTEM = '-apple-system, "SF Pro Text", "Helvetica Neue", system-ui, sans-serif';

/* ── iOS ── */
const STATUS_H = 54;
const SAFARI_BAR_H = 76;
/** Safari's page viewport, between the status bar and the toolbar. */
const VIEWPORT_H = PHONE_SCREEN_H - SAFARI_BAR_H;
const IOS_BLUE = '#0A84FF';

/* The real sheet's constants (V2MobileTicketBar). */
const SHEET_EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';
const SHEET_DURATION = '0.5s';
const SYSTEM_EASE = 'cubic-bezier(0.2, 0.8, 0.2, 1)';

/** Tailwind's sizes as the product uses them, so the markup reads like the source. */
const t = {
  xs: { fontSize: 12, lineHeight: '16px' },
  sm: { fontSize: 14, lineHeight: '20px' },
  base: { fontSize: 16, lineHeight: '24px' },
  xl: { fontSize: 20, lineHeight: '28px' },
  x2: { fontSize: 24, lineHeight: '32px' },
  x3: { fontSize: 30, lineHeight: '36px' },
} as const;

const heading: CSSProperties = { fontFamily: FONT, fontWeight: 700, color: TEXT, margin: 0 };

/* ───────────────────────── iOS chrome ───────────────────────── */

/** Status bar over the page, with the Dynamic Island. */
function StatusBar({ dark }: { dark: boolean }) {
  const ink = dark ? '#FFFFFF' : '#000000';
  return (
    <div style={{ position: 'absolute', left: 0, right: 0, top: 0, height: STATUS_H, zIndex: 38, pointerEvents: 'none', fontFamily: SYSTEM, color: ink, transition: 'color .3s ease' }}>
      <span style={{ position: 'absolute', left: 50, top: 18, fontSize: 16, fontWeight: 600, letterSpacing: '-0.01em' }}>9:41</span>
      <span style={{ position: 'absolute', left: '50%', top: 11, width: 124, height: 36, marginLeft: -62, borderRadius: 20, background: '#000000' }} />
      <span style={{ position: 'absolute', right: 30, top: 20, display: 'flex', alignItems: 'center', gap: 6 }}>
        <svg width="18" height="12" viewBox="0 0 18 12" fill={ink} aria-hidden>
          <rect x="0" y="8" width="3" height="4" rx="1" /><rect x="5" y="5.5" width="3" height="6.5" rx="1" /><rect x="10" y="3" width="3" height="9" rx="1" /><rect x="15" y="0" width="3" height="12" rx="1" />
        </svg>
        <svg width="16" height="12" viewBox="0 0 16 12" fill={ink} aria-hidden>
          <path d="M8 2.3c2.2 0 4.2.9 5.7 2.3l1.1-1.1A9.6 9.6 0 0 0 8 .7 9.6 9.6 0 0 0 1.2 3.5l1.1 1.1A8 8 0 0 1 8 2.3Zm0 3.1c1.4 0 2.6.5 3.5 1.4l1.1-1.1A6.5 6.5 0 0 0 8 3.8a6.5 6.5 0 0 0-4.6 1.9l1.1 1.1A4.9 4.9 0 0 1 8 5.4Zm0 3.2c.6 0 1.1.2 1.4.6L8 10.6 6.6 9.2c.3-.4.8-.6 1.4-.6Z" />
        </svg>
        <span style={{ width: 27, height: 13, borderRadius: 4, border: `1.2px solid ${dark ? 'rgba(255,255,255,.45)' : 'rgba(0,0,0,.4)'}`, padding: 1.5, boxSizing: 'border-box', display: 'flex' }}>
          <span style={{ flex: 1, borderRadius: 2, background: ink }} />
        </span>
      </span>
    </div>
  );
}

/** iOS 26 Safari's floating bottom bar, carrying the organiser's own domain. */
function SafariBar() {
  return (
    <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: SAFARI_BAR_H, zIndex: 10, background: PAGE_BG, fontFamily: SYSTEM }}>
      <div
        style={{
          position: 'absolute', left: 14, right: 14, top: 6, height: 46, borderRadius: 23,
          background: 'rgba(58,62,72,0.72)', boxShadow: 'inset 0 0 0 0.5px rgba(255,255,255,0.18), 0 4px 18px rgba(0,0,0,0.35)',
          display: 'flex', alignItems: 'center', padding: '0 8px', color: '#FFFFFF',
        }}
      >
        <span style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ChevronLeft size={22} strokeWidth={2.2} />
        </span>
        <span style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, fontSize: 16, fontWeight: 500 }}>
          <Lock size={12} strokeWidth={2.4} style={{ opacity: 0.7 }} />
          {ORG.domain}
        </span>
        <span style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
          {[0, 1, 2].map((i) => <span key={i} style={{ width: 4, height: 4, borderRadius: 2, background: '#FFFFFF' }} />)}
        </span>
      </div>
      <HomeIndicator dark />
    </div>
  );
}

function HomeIndicator({ dark }: { dark: boolean }) {
  return <span style={{ position: 'absolute', left: '50%', bottom: 8, width: 134, height: 5, marginLeft: -67, borderRadius: 3, background: dark ? '#FFFFFF' : '#000000', opacity: 0.9 }} />;
}

/* ───────────────────────── Themed page pieces ───────────────────────── */

/** ThemedEventPage's accent-glow background effect, fixed to the viewport. */
function AccentGlow() {
  return (
    <div
      aria-hidden
      style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: [
          `radial-gradient(ellipse 70% 45% at 15% 0%, color-mix(in srgb, ${ACCENT} 50%, transparent), transparent)`,
          `radial-gradient(ellipse 50% 30% at 80% 5%, color-mix(in srgb, ${ACCENT} 25%, transparent), transparent)`,
          `radial-gradient(ellipse 40% 25% at 50% 50%, color-mix(in srgb, ${ACCENT} 10%, transparent), transparent)`,
        ].join(', '),
      }}
    />
  );
}

/** ThemedEventPage's header: logo left (name hidden on mobile), "Powered by Fitvent" right. */
function BrandHeader() {
  return (
    <header style={{ borderBottom: '1px solid var(--fv-border)' }}>
      <div style={{ padding: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={ORG.logo} alt="" style={{ height: 32, width: 32 * ORG.logoAspect, display: 'block', objectFit: 'contain' }} />
        <span style={{ ...t.xs, color: '#FFFFFF', whiteSpace: 'nowrap' }}>Powered by Fitvent</span>
      </div>
    </header>
  );
}

/**
 * One Safari page: the themed ground under the status bar down to the toolbar.
 * Opaque, so a page fading in covers the one under it.
 */
function Page({ children, style, scrollRef }: { children: ReactNode; style?: CSSProperties; scrollRef?: React.Ref<HTMLDivElement> }) {
  return (
    <div style={{ position: 'absolute', left: 0, right: 0, top: 0, height: VIEWPORT_H, overflow: 'hidden', background: PAGE_BG, ...style }}>
      <AccentGlow />
      <div ref={scrollRef} style={{ position: 'relative', paddingTop: STATUS_H, transition: `transform 1s ${SYSTEM_EASE}` }}>
        <BrandHeader />
        {children}
      </div>
    </div>
  );
}

/* ───────────────────────── 1. Event page + ticket sheet ───────────────────────── */

function Pill({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 9999, ...t.xs, fontWeight: 500, ...style }}>
      {children}
    </span>
  );
}

const neutralPill: CSSProperties = { backgroundColor: 'var(--fv-bg-raised)', color: 'var(--fv-text-secondary)', border: '1px solid var(--fv-border)' };

function EventPage() {
  const avatars = ['JO', 'AJ', 'LM'];
  return (
    <>
      {/* V2EventHero */}
      <div style={{ padding: '24px 16px 32px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
          <Pill style={{ backgroundColor: 'rgba(34, 197, 94, 0.12)', color: '#22c55e', border: '1px solid rgba(34, 197, 94, 0.35)' }}>
            <span style={{ position: 'relative', display: 'flex', width: 8, height: 8 }}>
              <span className="animate-ping" style={{ position: 'absolute', display: 'inline-flex', width: '100%', height: '100%', borderRadius: 9999, background: '#4ade80', opacity: 0.75 }} />
              <span style={{ position: 'relative', display: 'inline-flex', width: 8, height: 8, borderRadius: 9999, background: '#22c55e' }} />
            </span>
            Live
          </Pill>
          <Pill style={neutralPill}><Activity size={12} />{EVENT.activityLabel}</Pill>
          <Pill style={neutralPill}>{EVENT.nature}</Pill>
        </div>

        <h1 style={{ ...heading, ...t.x3, lineHeight: 1.1, marginBottom: 16, color: 'var(--fv-theme-text)' }}>{EVENT.name}</h1>

        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 20, rowGap: 8, color: 'var(--fv-theme-text-muted)' }}>
          {[
            { Icon: Calendar, label: EVENT.dateLong },
            { Icon: Clock, label: EVENT.time },
            { Icon: MapPin, label: EVENT.venue },
          ].map(({ Icon, label }) => (
            <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, ...t.sm }}>
              <Icon size={14} style={{ color: ACCENT, flex: 'none' }} />
              {label}
            </span>
          ))}
        </div>

        <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ display: 'flex' }}>
            {avatars.map((initials, i) => (
              <div
                key={initials}
                style={{
                  width: 24, height: 24, borderRadius: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700,
                  marginLeft: i === 0 ? 0 : -6, backgroundColor: ACCENT, color: ON_ACCENT,
                  filter: `hue-rotate(${[0, 30, -25][i]}deg)`,
                  // Transparent box: --fv-bg is transparent, so the ring falls back to the page.
                  boxShadow: `0 0 0 1.5px ${PAGE_BG}`,
                }}
              >
                {initials}
              </div>
            ))}
          </div>
          <span style={{ ...t.xs, color: 'var(--fv-theme-text-muted)' }}>+ 61 more registered</span>
        </div>
      </div>

      <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 24 }}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src="/website/uploads/venue-evolve.jpg" alt="" style={{ display: 'block', width: '100%', height: 'auto', aspectRatio: '1000 / 667', borderRadius: 18, border: '1px solid var(--fv-border)' }} />
        <div style={{ borderRadius: 8, border: '1px solid var(--fv-border)', padding: 24 }}>
          <h2 style={{ ...heading, ...t.xl, marginBottom: 12 }}>About This Event</h2>
          <p style={{ margin: 0, fontSize: 16, lineHeight: 1.5, color: TEXT_MUTED }}>{EVENT.about}</p>
        </div>
      </div>
    </>
  );
}

function Radio({ on }: { on: boolean }) {
  return (
    <div
      style={{
        width: 16, height: 16, borderRadius: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none',
        border: on ? 'none' : '1.5px solid var(--fv-border-strong)', background: on ? ACCENT : 'transparent',
        transition: 'background .15s ease',
      }}
    >
      {on && <div style={{ width: 6, height: 6, borderRadius: 9999, backgroundColor: ON_ACCENT }} />}
    </div>
  );
}

/** V2MobileTicketBar, collapsed ("Get Tickets") or expanded ("Select Ticket"). */
function TicketBar({ expanded, pairsSelected, press }: { expanded: boolean; pairsSelected: boolean; press: PhonePress }) {
  // Transparent box on a dark page: glass of the page colour, light text.
  const glassBg = `color-mix(in srgb, ${PAGE_BG} 82%, transparent)`;
  const textPrimary = '#e5e5e5';
  const textMuted = '#9c9590';
  const solidBorder = 'rgba(255,255,255,0.08)';
  const ease = `${SHEET_DURATION} ${SHEET_EASE}`;
  const pressed = press === 'getTickets' || press === 'continue';

  return (
    <>
      <div style={{ position: 'absolute', inset: 0, zIndex: 1, background: 'rgba(0,0,0,0.6)', opacity: expanded ? 1 : 0, transition: `opacity ${ease}` }} />
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 2, fontFamily: FONT }}>
        <div style={{ height: 32, background: `linear-gradient(to bottom, ${PAGE_BG}00, ${PAGE_BG}99)`, opacity: expanded ? 0 : 1, transition: `opacity ${ease}` }} />
        <div
          style={{
            background: expanded ? PAGE_BG : glassBg,
            backdropFilter: expanded ? 'none' : 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: expanded ? 'none' : 'blur(20px) saturate(180%)',
            borderTop: '1px solid rgba(255,255,255,0.12)',
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            boxShadow: '0 -16px 48px rgba(0,0,0,0.7), 0 -4px 12px rgba(0,0,0,0.4)',
            maxHeight: expanded ? 0.85 * VIEWPORT_H : 160,
            overflow: 'hidden',
            transition: `max-height ${ease}, background ${ease}`,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 4px' }}>
            <div style={{ width: 40, height: 4, borderRadius: 9999, background: 'rgba(255,255,255,0.15)' }} />
          </div>

          <div style={{ maxHeight: expanded ? 44 : 0, opacity: expanded ? 1 : 0, overflow: 'hidden', transition: `max-height ${ease}, opacity 0.3s ease` }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px 12px', borderBottom: `1px solid ${solidBorder}` }}>
              <h2 style={{ margin: 0, ...t.base, fontWeight: 600, color: textPrimary }}>Select Ticket</h2>
              <span style={{ width: 32, height: 32, borderRadius: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.08)' }}>
                <ChevronDown size={18} style={{ color: textMuted }} />
              </span>
            </div>
          </div>

          {/* V2TicketPanel, simple list, embedded with its button hidden */}
          <div style={{ maxHeight: expanded ? 400 : 0, opacity: expanded ? 1 : 0, overflow: 'hidden', transition: `max-height ${ease}, opacity 0.35s ease 0.1s` }}>
            {TICKETS.map((ticket, i) => {
              const on = ticket.key === 'pairs' && pairsSelected;
              return (
                <div
                  key={ticket.key}
                  data-hero-target={ticket.key === 'pairs' ? 'pairs' : undefined}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px',
                    background: on ? 'var(--fv-accent-bg)' : 'transparent',
                    borderBottom: i < TICKETS.length - 1 ? '1px solid var(--fv-border)' : undefined,
                    transition: 'background .15s ease',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Radio on={on} />
                    <div>
                      <p style={{ margin: 0, ...t.sm, fontWeight: 600, color: 'var(--fv-text-primary)' }}>{ticket.name}</p>
                      {'remaining' in ticket && (
                        <p style={{ margin: '2px 0 0', fontSize: 11, lineHeight: '16px', color: 'var(--fv-text-muted)' }}>{ticket.remaining}</p>
                      )}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ margin: 0, ...t.sm, fontWeight: 700, color: 'var(--fv-text-primary)' }}>{ticket.price}</p>
                    {'perTeam' in ticket && (
                      <p style={{ margin: '2px 0 0', fontSize: 10, lineHeight: '15px', color: 'var(--fv-text-muted)' }}>per team</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ maxHeight: expanded ? 0 : 40, opacity: expanded ? 0 : 1, overflow: 'hidden', transition: `max-height ${ease}, opacity 0.2s ease` }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '16px 20px 0' }}>
              <p style={{ margin: 0, fontSize: 13, lineHeight: '20px', fontWeight: 500, color: textMuted, maxWidth: '55%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {EVENT.name}
              </p>
              <p style={{ margin: 0, fontSize: 15, lineHeight: '22px', fontWeight: 700, color: textPrimary }}>From £80</p>
            </div>
          </div>

          <div style={{ padding: `${expanded ? 16 : 12}px 20px 20px`, transition: `padding ${ease}` }}>
            <div
              data-hero-target={expanded ? 'continue' : 'getTickets'}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: 52, borderRadius: 9999,
                fontSize: 15, fontWeight: 600, backgroundColor: ACCENT, color: ON_ACCENT,
                boxShadow: '0 2px 8px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.15)',
                opacity: expanded && !pairsSelected ? 0.35 : 1,
                transform: `scale(${pressed ? 0.98 : 1})`,
                transition: 'transform 0.15s ease, opacity 0.3s ease',
              }}
            >
              {expanded ? 'Continue to Registration' : 'Get Tickets'}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/* ───────────────────────── 2. Team payment ───────────────────────── */

function AppleMark({ size = 15, color = 'currentColor' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size * 1.2} viewBox="0 0 17 20" aria-hidden style={{ display: 'block', marginTop: -3 }}>
      <path fill={color} d="M14.1 10.6c0-2.6 2.1-3.8 2.2-3.9-1.2-1.8-3.1-2-3.7-2-1.6-.2-3.1.9-3.9.9-.8 0-2-.9-3.4-.9C3.6 4.8 2 5.8 1.1 7.4c-1.9 3.2-.5 8 1.3 10.6.9 1.3 1.9 2.7 3.3 2.6 1.3-.1 1.8-.8 3.4-.8s2 .8 3.4.8c1.4 0 2.3-1.3 3.2-2.6 1-1.5 1.4-2.9 1.4-3-.1 0-2.9-1.1-3-4.4ZM11.6 3c.7-.9 1.2-2.1 1.1-3.3-1 0-2.3.7-3 1.6-.7.8-1.3 2-1.1 3.2 1.1.1 2.3-.6 3-1.5Z" />
    </svg>
  );
}

function PaymentPage({ press }: { press: PhonePress }) {
  const card: CSSProperties = { borderRadius: 8, border: '1px solid var(--fv-border)', padding: 16 };
  return (
    <div style={{ padding: '32px 16px', display: 'flex', flexDirection: 'column', gap: 24, fontFamily: FONT, color: TEXT }}>
      <div>
        <h1 style={{ ...heading, ...t.x2 }}>Team Payment</h1>
        <p style={{ margin: '4px 0 0', ...t.sm, color: TEXT_MUTED }}>
          You&apos;re covering the full team ticket. You&apos;ll be charged now and the others activate their tickets via a link in their inbox.
        </p>
      </div>

      <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Users size={16} style={{ color: TEXT_MUTED }} />
          <span style={{ ...t.sm, fontWeight: 600 }}>Your team</span>
        </div>
        <div style={{ ...t.sm, color: TEXT_MUTED, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>•</span> {ATHLETE.partnerEmail}
        </div>
        <div style={{ ...t.xs, color: TEXT_MUTED, paddingTop: 4 }}>Activation invitations go out as soon as you&apos;ve paid.</div>
      </div>

      <div style={{ ...card, background: 'var(--fv-bg-secondary)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 600 }}>
        <span style={t.base}>Total to pay</span>
        <span style={{ ...t.xl, color: ACCENT }}>{ATHLETE.total}</span>
      </div>

      {/* HoldCountdown */}
      <div style={{ ...card, padding: 12, ...t.xs, color: TEXT_MUTED }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontWeight: 600, color: TEXT }}>Your place is held</span>
          <span style={{ fontFamily: MONO, fontSize: 14, color: TEXT }}>9:41</span>
        </div>
        <p style={{ margin: '4px 0 0' }}>We are holding your place while you pay. If the timer runs out it goes back on sale and nothing is charged.</p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* ExpressCheckoutElement: Apple Pay, "buy", white on a dark page */}
        <div
          data-hero-target="applePay"
          style={{
            height: 44, borderRadius: 6, background: '#FFFFFF', color: '#000000', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
            fontFamily: SYSTEM, fontSize: 17, fontWeight: 500, letterSpacing: '-0.01em',
            transform: `scale(${press === 'applePay' ? 0.97 : 1})`, filter: `brightness(${press === 'applePay' ? 0.88 : 1})`,
            transition: 'transform .12s ease, filter .12s ease',
          }}
        >
          Buy with <AppleMark /><span style={{ fontWeight: 600 }}>Pay</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, ...t.xs, textTransform: 'uppercase', letterSpacing: '0.025em', color: TEXT_MUTED }}>
          <div style={{ flex: 1, height: 1, background: 'var(--fv-border)' }} />
          <span>Or pay with card</span>
          <div style={{ flex: 1, height: 1, background: 'var(--fv-border)' }} />
        </div>

        {/* PaymentElement, accordion, card only, Stripe's night theme */}
        <div style={{ height: 52, borderRadius: 5, border: '1px solid rgba(255,255,255,0.18)', background: PAGE_BG, display: 'flex', alignItems: 'center', gap: 12, padding: '0 16px', ...t.sm, color: TEXT }}>
          <span style={{ width: 16, height: 16, borderRadius: 9999, border: '1.5px solid rgba(255,255,255,0.4)', boxSizing: 'border-box' }} />
          <svg width="22" height="16" viewBox="0 0 22 16" fill="none" stroke={TEXT_MUTED} strokeWidth="1.4" aria-hidden><rect x="1" y="1" width="20" height="14" rx="2.5" /><path d="M1 5.5h20" /></svg>
          Card
        </div>
      </div>
    </div>
  );
}

/** iOS's Apple Pay sheet, over the whole screen. */
function ApplePaySheetView({ state }: { state: ApplePaySheet }) {
  const open = state !== 'off';
  const done = state === 'done';
  return (
    <>
      <div style={{ position: 'absolute', inset: 0, zIndex: 30, background: 'rgba(0,0,0,0.42)', opacity: open ? 1 : 0, transition: 'opacity .35s ease' }} />
      <div
        style={{
          position: 'absolute', left: 8, right: 8, bottom: 8, zIndex: 31, borderRadius: 38, background: '#F2F2F7', color: '#000000', fontFamily: SYSTEM,
          padding: '20px 20px 26px', boxShadow: '0 -8px 40px rgba(0,0,0,0.3)',
          transform: `translateY(${open ? 0 : 110}%)`, transition: `transform .45s ${SYSTEM_EASE}`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 2, fontSize: 22, fontWeight: 600 }}><AppleMark size={18} color="#000" />Pay</span>
          <span style={{ width: 30, height: 30, borderRadius: 15, background: '#E3E3E8', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6D6D72' }}><X size={16} strokeWidth={2.6} /></span>
        </div>
        <div style={{ marginTop: 18, borderRadius: 14, background: '#FFFFFF', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ width: 44, height: 30, borderRadius: 5, background: 'linear-gradient(135deg, #1F2A5A, #3C4FA0)', color: '#FFFFFF', fontSize: 10, fontWeight: 800, fontStyle: 'italic', display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end', padding: '0 5px 4px', boxSizing: 'border-box' }}>VISA</span>
          <span style={{ flex: 1 }}>
            <span style={{ display: 'block', fontSize: 16, fontWeight: 500 }}>Visa Debit</span>
            <span style={{ display: 'block', fontSize: 14, color: '#6D6D72' }}>•••• 4242</span>
          </span>
          <ChevronDown size={18} style={{ color: '#C4C4C7', transform: 'rotate(-90deg)' }} />
        </div>
        <div style={{ marginTop: 16, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '0 4px' }}>
          <span style={{ fontSize: 15, color: '#6D6D72' }}>Pay {ORG.name}</span>
          <span style={{ fontSize: 26, fontWeight: 600, letterSpacing: '-0.02em' }}>{ATHLETE.total}</span>
        </div>
        <div style={{ marginTop: 22, height: 70, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          {done ? (
            <>
              <span style={{ width: 44, height: 44, borderRadius: 22, background: IOS_BLUE, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="m5 12.5 4.5 4.5L19 7" /></svg>
              </span>
              <span style={{ fontSize: 15, fontWeight: 600 }}>Done</span>
            </>
          ) : (
            <>
              <svg className="fvh-faceid" width="44" height="44" viewBox="0 0 44 44" fill="none" stroke={IOS_BLUE} strokeWidth="2.4" strokeLinecap="round" aria-hidden>
                <path d="M4 14V9a5 5 0 0 1 5-5h5M30 4h5a5 5 0 0 1 5 5v5M40 30v5a5 5 0 0 1-5 5h-5M14 40H9a5 5 0 0 1-5-5v-5" />
                <path d="M15 16v4M29 16v4M22 17v8h-2M16 30c3.5 3 8.5 3 12 0" />
              </svg>
              <span style={{ fontSize: 15, color: '#6D6D72' }}>Confirm with Side Button</span>
            </>
          )}
        </div>
      </div>
      {/* The side-button prompt iOS draws at the screen edge */}
      <div
        style={{
          position: 'absolute', right: 0, top: 150, zIndex: 32, width: 76, borderRadius: '14px 0 0 14px', background: 'rgba(28,28,30,0.92)', color: '#FFFFFF',
          fontFamily: SYSTEM, fontSize: 12, fontWeight: 600, lineHeight: 1.25, textAlign: 'center', padding: '12px 8px', boxSizing: 'border-box',
          opacity: state === 'confirm' ? 1 : 0, transform: `translateX(${state === 'confirm' ? 0 : 20}px)`, transition: 'opacity .3s ease, transform .4s ease',
        }}
      >
        Double Click to Pay
      </div>
    </>
  );
}

/* ───────────────────────── 3. You're in ───────────────────────── */

// The first five cards of the product's picker, in its order (lib/social/stories/templates).
const STORY_STYLES = ['minimal', 'bib', 'startlist', 'countdown', 'startline'] as const;
const STORY_LABELS: Record<(typeof STORY_STYLES)[number], string> = { minimal: 'Minimal', bib: 'Race bib', startlist: 'Start list', countdown: 'Countdown', startline: 'Start line' };

/**
 * The story card, drawn by the product's own renderStoryToCanvas with
 * est.28's colours (resolveStoryBrandingColors: accent = theme secondary,
 * secondary = theme primary). Rendered once, when `warm`, off the main path.
 */
function useStoryImages(warm: boolean) {
  const [images, setImages] = useState<{ preview?: string; thumbs: Record<string, string> }>({ thumbs: {} });
  useEffect(() => {
    if (!warm) return;
    let cancelled = false;
    (async () => {
      const { renderStoryToCanvas } = await import('@/lib/social/story-templates');
      // The template sends logos through the image proxy, which only takes
      // absolute URLs (organisers' logos live in storage). Ours is a local
      // file, so hand it over inline; data: URLs skip the proxy.
      const logoUrl = await fetch(ORG.logo)
        .then((r) => r.blob())
        .then((blob) => new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result));
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        }))
        .catch(() => undefined);
      const data = {
        eventName: EVENT.name,
        eventDate: EVENT.start,
        eventTime: '8:30 AM',
        eventTimezone: EVENT.timezone,
        eventLocation: EVENT.venue,
        participantName: ATHLETE.name,
        ticketTypeName: ATHLETE.ticket,
        waveName: 'Wave 3',
        waveTime: '08:30',
        bibNumber: '214',
        accentColor: ACCENT,
        secondaryColor: PAGE_BG,
        logoUrl,
        activityType: EVENT.activityType,
      };
      const canvas = document.createElement('canvas');
      // Minimal is drawn with no background, to sit over a photo. Saved as a
      // JPEG that transparency turns black, so flatten each render onto
      // est.28's navy first.
      const flat = document.createElement('canvas');
      const flatten = () => {
        flat.width = canvas.width;
        flat.height = canvas.height;
        const ctx = flat.getContext('2d')!;
        ctx.fillStyle = PAGE_BG;
        ctx.fillRect(0, 0, flat.width, flat.height);
        ctx.drawImage(canvas, 0, 0);
        return flat;
      };
      const thumbs: Record<string, string> = {};
      let preview: string | undefined;
      for (const style of STORY_STYLES) {
        if (cancelled) return;
        try {
          await renderStoryToCanvas(canvas, data, style);
          const out = flatten();
          if (style === 'minimal') preview = out.toDataURL('image/jpeg', 0.82);
          thumbs[style] = out.toDataURL('image/jpeg', 0.5);
        } catch {
          // A failed template just shows its placeholder, as the product does.
        }
        // Give the main thread back between the full-size renders.
        await new Promise((r) => setTimeout(r, 30));
      }
      if (!cancelled) setImages({ preview, thumbs });
    })();
    return () => { cancelled = true; };
  }, [warm]);
  return images;
}

function SuccessPage({ press, warm, storyRef, walletRef }: { press: PhonePress; warm: boolean; storyRef: React.Ref<HTMLDivElement>; walletRef: React.Ref<HTMLDivElement> }) {
  const story = useStoryImages(warm);
  const placeholder: CSSProperties = { background: 'linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)', backgroundColor: 'var(--fv-bg-raised)' };
  const outlined: CSSProperties = { border: `2px solid ${ACCENT}`, color: ACCENT, background: 'transparent' };
  return (
    <div style={{ padding: '32px 16px', fontFamily: FONT }}>
      <div style={{ borderRadius: 8, padding: 32, border: '1px solid var(--fv-border)', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'center' }}>
          <div style={{ width: 64, height: 64, borderRadius: 9999, backgroundColor: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="32" height="32" fill="none" stroke="#FFFFFF" viewBox="0 0 24 24" aria-hidden><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          </div>
        </div>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <h1 style={{ ...heading, ...t.x3, marginBottom: 8 }}>You&apos;re in</h1>
          <p style={{ margin: 0, ...t.base, color: TEXT_MUTED }}>Ticket confirmed. We&apos;ve emailed you a copy.</p>
        </div>

        {/* InstagramStoryShare, expanded (defaultExpanded on this page) */}
        <div ref={storyRef} style={{ marginBottom: 24, marginTop: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRadius: '12px 12px 0 0', padding: '12px 16px', background: ACCENT, color: '#FFFFFF' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, ...t.sm, fontWeight: 600 }}><ImageIcon size={16} />Share your registration</span>
            <X size={16} />
          </div>
          <div style={{ borderRadius: '0 0 12px 12px', padding: 16, display: 'flex', flexDirection: 'column', gap: 16, background: 'var(--fv-bg-raised)', border: '1px solid var(--fv-border)', borderTop: 'none' }}>
            <div style={{ display: 'flex', gap: 8, overflow: 'hidden' }}>
              {STORY_STYLES.map((s, i) => (
                <div key={s} style={{ position: 'relative', flex: 'none', width: 64, height: 114, borderRadius: 8, overflow: 'hidden', border: i === 0 ? `3px solid ${ACCENT}` : '2px solid var(--fv-border)', opacity: i === 0 ? 1 : 0.7, boxSizing: 'border-box' }}>
                  {story.thumbs[s]
                    // eslint-disable-next-line @next/next/no-img-element
                    ? <img src={story.thumbs[s]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    : <div style={{ width: '100%', height: '100%', ...placeholder }} />}
                  {i === 0 && <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '2px 0', textAlign: 'center', fontSize: 8, fontWeight: 700, background: ACCENT, color: '#FFFFFF' }}>{STORY_LABELS[s]}</div>}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div style={{ position: 'relative', overflow: 'hidden', borderRadius: 12, width: 220, height: 391, background: PAGE_BG, boxShadow: '0 4px 24px rgba(0,0,0,0.15)' }}>
                {story.preview && (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={story.preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                )}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, borderRadius: 9999, padding: '12px 16px', ...t.sm, fontWeight: 600, background: ACCENT, color: '#FFFFFF' }}>
                <Instagram size={16} />Share to Instagram Story
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, borderRadius: 9999, padding: '10px 16px', ...t.xs, fontWeight: 600, ...outlined }}><Copy size={14} />Copy</div>
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, borderRadius: 9999, padding: '10px 16px', ...t.xs, fontWeight: 600, ...outlined }}><Download size={14} />Save</div>
              </div>
            </div>
            <p style={{ margin: 0, ...t.xs, textAlign: 'center', color: TEXT_MUTED }}>Share opens Instagram directly. Copy saves the image to paste anywhere.</p>
          </div>
        </div>

        {/* WalletButtons, with the real badge artwork */}
        <div ref={walletRef} style={{ marginBottom: 24, borderRadius: 8, padding: 24, border: '1px solid var(--fv-border)' }}>
          <div style={{ ...t.xs, textTransform: 'uppercase', letterSpacing: '0.05em', color: TEXT_MUTED, marginBottom: 12, textAlign: 'center' }}>Add to wallet</div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              data-hero-target="addWallet"
              src="/wallet-buttons/add-to-apple-wallet.svg"
              alt=""
              width={240}
              height={50}
              style={{ display: 'block', transform: `scale(${press === 'addWallet' ? 0.96 : 1})`, transition: 'transform .12s ease' }}
            />
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/wallet-buttons/add-to-google-wallet.svg" alt="" width={240} height={50} style={{ display: 'block' }} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── Wallet, notification, Mail ───────────────────────── */

/** The est.28 pass, laid out the way lib/wallet/apple-wallet.ts builds it. */
function WalletPass() {
  const ink = '#1B1200';
  const label: CSSProperties = { fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', color: 'rgba(27,18,0,0.62)' };
  const value: CSSProperties = { fontSize: 17, fontWeight: 500, color: ink, marginTop: 2 };
  return (
    <div style={{ width: 330, borderRadius: 16, background: ACCENT, color: ink, fontFamily: SYSTEM, boxShadow: '0 12px 30px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
      <div style={{ padding: '16px 18px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={ORG.logo} alt="" style={{ height: 20, width: 20 * ORG.logoAspect, display: 'block', filter: 'brightness(0)' }} />
        <span style={{ ...label, fontSize: 13 }}>EVENT TICKET</span>
      </div>
      <div style={{ padding: '14px 18px 0' }}>
        <div style={label}>EVENT</div>
        <div style={{ fontSize: 24, fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.15, marginTop: 2 }}>{EVENT.name}</div>
      </div>
      <div style={{ padding: '14px 18px 0', display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 12 }}>
        <div><div style={label}>DATE</div><div style={value}>Sat 11 Sept</div><div style={{ fontSize: 14, color: 'rgba(27,18,0,0.7)' }}>8:30 am</div></div>
        <div><div style={label}>VENUE</div><div style={value}>{ORG.name}</div><div style={{ fontSize: 14, color: 'rgba(27,18,0,0.7)' }}>{EVENT.venueShort}</div></div>
        <div><div style={label}>DIVISION</div><div style={value}>{ATHLETE.division}</div></div>
        <div><div style={label}>PARTICIPANT</div><div style={value}>{ATHLETE.name}</div></div>
      </div>
      <div style={{ marginTop: 16, borderTop: '1px solid rgba(27,18,0,0.15)', padding: '18px 0 14px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
        <div style={{ background: '#FFFFFF', borderRadius: 12, padding: 12 }}>
          <svg width="132" height="132" viewBox={PASS_QR.viewBox} shapeRendering="crispEdges" aria-hidden>
            <path d={PASS_QR.d} stroke="#000000" strokeWidth="1" />
          </svg>
        </div>
        <span style={{ fontFamily: MONO, fontSize: 12, color: 'rgba(27,18,0,0.7)' }}>{ATHLETE.ticketReference}</span>
      </div>
    </div>
  );
}

/** Wallet's "add pass" sheet. */
function PassSheet({ open, press }: { open: boolean; press: PhonePress }) {
  return (
    <>
      <div style={{ position: 'absolute', inset: 0, zIndex: 30, background: 'rgba(0,0,0,0.42)', opacity: open ? 1 : 0, transition: 'opacity .35s ease' }} />
      <div
        style={{
          position: 'absolute', left: 0, right: 0, top: 58, bottom: 0, zIndex: 31, borderRadius: '14px 14px 0 0', background: '#F2F2F7', fontFamily: SYSTEM,
          transform: `translateY(${open ? 0 : 105}%)`, transition: `transform .5s ${SYSTEM_EASE}`,
        }}
      >
        <div style={{ height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 18px', fontSize: 17 }}>
          <span style={{ color: IOS_BLUE }}>Cancel</span>
          <span style={{ fontWeight: 600, color: '#000000' }}>{ORG.name}</span>
          <span
            data-hero-target="passAdd"
            style={{ color: IOS_BLUE, fontWeight: 600, padding: '6px 4px', opacity: press === 'passAdd' ? 0.4 : 1, transition: 'opacity .12s ease' }}
          >
            Add
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 12 }}>
          <WalletPass />
        </div>
      </div>
    </>
  );
}

function MailIcon({ size }: { size: number }) {
  return (
    <span style={{ flex: 'none', width: size, height: size, borderRadius: size * 0.23, background: 'linear-gradient(180deg, #1E9BFF, #0A62F0)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width={size * 0.6} height={size * 0.42} viewBox="0 0 24 17" fill="none" aria-hidden>
        <rect x="0.5" y="0.5" width="23" height="16" rx="2.5" fill="#FFFFFF" />
        <path d="M1.5 1.8 12 10l10.5-8.2" stroke="#1583F7" strokeWidth="1.6" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

/** The confirmation email's notification banner. */
function MailNotification({ open, press }: { open: boolean; press: PhonePress }) {
  return (
    <div
      data-hero-target="notification"
      style={{
        position: 'absolute', left: 10, right: 10, top: 12, zIndex: 40, borderRadius: 24, padding: '12px 14px',
        background: 'rgba(242,242,247,0.86)', backdropFilter: 'blur(24px) saturate(180%)', WebkitBackdropFilter: 'blur(24px) saturate(180%)',
        boxShadow: '0 10px 30px rgba(0,0,0,0.3)', display: 'flex', gap: 11, fontFamily: SYSTEM, color: '#000000',
        opacity: open ? 1 : 0, transform: `translateY(${open ? 0 : -130}%) scale(${press === 'notification' ? 0.97 : 1})`,
        transition: `transform .55s ${SYSTEM_EASE}, opacity .3s ease`,
      }}
    >
      <MailIcon size={38} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{ flex: 1, fontSize: 15, fontWeight: 600 }}>{ORG.name}</span>
          <span style={{ fontSize: 13, color: 'rgba(60,60,67,0.6)' }}>now</span>
        </span>
        <span style={{ display: 'block', fontSize: 15, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Your ticket for {EVENT.name}</span>
        <span style={{ fontSize: 15, lineHeight: 1.3, color: 'rgba(0,0,0,0.75)', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          Hi {ATHLETE.name}, you&apos;re in for {EVENT.name}. See you on race day.
        </span>
      </span>
    </div>
  );
}

const EMAIL_FRAME_H = 1900;

/**
 * Mail, open on the confirmation email. The email is the product's own
 * template (see lib/website-hero/confirmation-email.ts) in an iframe, so it
 * lays out at a phone's width with its own mobile styles, exactly as an inbox
 * would show it.
 */
function MailView({ open, scrolled, warm }: { open: boolean; scrolled: boolean; warm: boolean }) {
  const [html, setHtml] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  useEffect(() => {
    if (!warm) return;
    let cancelled = false;
    import('@/lib/website-hero/confirmation-email').then(({ buildConfirmationEmailHtml, CONFIRMATION_SUBJECT }) => {
      if (cancelled) return;
      setHtml(buildConfirmationEmailHtml());
      setSubject(CONFIRMATION_SUBJECT);
    });
    return () => { cancelled = true; };
  }, [warm]);

  return (
    <div
      style={{
        position: 'absolute', inset: 0, zIndex: 35, background: '#FFFFFF', fontFamily: SYSTEM, color: '#000000', overflow: 'hidden',
        transform: `translateY(${open ? 0 : 100}%)`, transition: `transform .55s ${SYSTEM_EASE}`,
      }}
    >
      <div style={{ paddingTop: STATUS_H, position: 'relative', zIndex: 2, background: '#FFFFFF' }}>
        <div style={{ height: 44, display: 'flex', alignItems: 'center', padding: '0 10px', color: IOS_BLUE, fontSize: 17 }}>
          <ChevronLeft size={26} strokeWidth={2.4} />
          <span>Inbox</span>
        </div>
        <div style={{ padding: '6px 16px 12px', borderBottom: '0.5px solid rgba(60,60,67,0.2)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ width: 40, height: 40, borderRadius: 20, background: PAGE_BG, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONT, fontWeight: 700, fontSize: 15 }}>28</span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ flex: 1, fontSize: 16, fontWeight: 600 }}>{ORG.name}</span>
                <span style={{ fontSize: 14, color: 'rgba(60,60,67,0.6)' }}>9:43</span>
              </span>
              <span style={{ display: 'block', fontSize: 14, color: 'rgba(60,60,67,0.6)' }}>To: {ATHLETE.name}</span>
            </span>
          </div>
          <div style={{ marginTop: 10, fontSize: 18, fontWeight: 600, lineHeight: 1.25 }}>{subject}</div>
        </div>
      </div>
      <div style={{ transform: `translateY(${scrolled ? -620 : 0}px)`, transition: `transform 2.4s cubic-bezier(.45,0,.2,1)` }}>
        {html && (
          <iframe
            title=""
            srcDoc={html}
            tabIndex={-1}
            scrolling="no"
            style={{ display: 'block', width: PHONE_SCREEN_W, height: EMAIL_FRAME_H, border: 0, pointerEvents: 'none' }}
          />
        )}
      </div>
      <HomeIndicator dark={false} />
    </div>
  );
}

/* ───────────────────────── The screen ───────────────────────── */

export interface HeroPhoneScreenProps {
  screen: PhoneScreen;
  pairsSelected: boolean;
  press: PhonePress;
  applePay: ApplePaySheet;
  doneScroll: DoneScroll;
  pass: boolean;
  notification: boolean;
  email: boolean;
  emailScrolled: boolean;
  /** Start loading the story card and the email: the phone has been seen. */
  warm: boolean;
}

export function HeroPhoneScreen(p: HeroPhoneScreenProps) {
  const onEvent = p.screen === 'event' || p.screen === 'sheet';
  const scrollerRef = useRef<HTMLDivElement>(null);
  const storyRef = useRef<HTMLDivElement>(null);
  const walletRef = useRef<HTMLDivElement>(null);

  // Scroll the confirmation page to the story card or the wallet. Written to
  // the node, with the distance on data-hero-scroll-y, so the cursor (which
  // measures after this runs) aims at where the page has scrolled to.
  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const target = p.doneScroll === 'story' ? storyRef.current : p.doneScroll === 'wallet' ? walletRef.current : null;
    const max = Math.max(0, scroller.offsetHeight - VIEWPORT_H);
    let y = 0;
    if (target) {
      // offsetTop up to the scroller, then leave a little of what's above.
      let top = 0;
      let node: HTMLElement | null = target;
      while (node && node !== scroller) { top += node.offsetTop; node = node.offsetParent as HTMLElement | null; }
      y = Math.min(max, Math.max(0, top - (p.doneScroll === 'story' ? 40 : 170)));
    }
    scroller.style.transform = `translateY(${-y}px)`;
    scroller.dataset.heroScrollY = String(y);
  }, [p.doneScroll]);

  const inMail = p.email;
  return (
    <div
      style={{
        position: 'relative', width: PHONE_SCREEN_W, height: PHONE_SCREEN_H, overflow: 'hidden', background: PAGE_BG,
        fontFamily: FONT, color: TEXT, textAlign: 'left', WebkitFontSmoothing: 'antialiased', ...THEME_VARS,
      }}
    >
      <StatusBar dark={!inMail} />

      <Page style={{ opacity: onEvent ? 1 : 0, transition: 'opacity .3s ease' }}>
        <EventPage />
      </Page>
      <div style={{ position: 'absolute', left: 0, right: 0, top: 0, height: VIEWPORT_H, overflow: 'hidden', opacity: onEvent ? 1 : 0, transition: 'opacity .3s ease' }}>
        <TicketBar expanded={p.screen === 'sheet'} pairsSelected={p.pairsSelected} press={p.press} />
      </div>

      <Page style={{ opacity: p.screen === 'pay' ? 1 : 0, transition: 'opacity .3s ease' }}>
        <PaymentPage press={p.press} />
      </Page>

      <Page scrollRef={scrollerRef} style={{ opacity: p.screen === 'done' ? 1 : 0, transition: 'opacity .35s ease' }}>
        <SuccessPage press={p.press} warm={p.warm} storyRef={storyRef} walletRef={walletRef} />
      </Page>

      <SafariBar />

      <ApplePaySheetView state={p.applePay} />
      <PassSheet open={p.pass} press={p.press} />
      <MailView open={p.email} scrolled={p.emailScrolled} warm={p.warm} />
      <MailNotification open={p.notification} press={p.press} />
    </div>
  );
}
